import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MainHeader from "../../components/common/MainHeader";
import { Form, Typography, DatePicker, List, Space, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import _ from "lodash";
import dayjs from "dayjs";
import { POST } from "../../utils/HttpClient";
import { URL_INTERFACE_AX, URL_POST_BAKING } from "../../constance/urls";
import { Toast } from "../../utils/Toast";
import InterfaceAXHistoryModal from "../../components/interface_ax/InterfaceAXHistoryModal";
import { INTERFACE_TYPE } from "../../constance/dataTypes";
import { formatComma } from "../../utils/stringFormat";
import TSTable from "../../components/common/TSTable";
import { DATE_FORMAT, HUMAN_DATE_FORMAT } from "../../constance/strings";
import ColorButton from '../../components/common/ColorButton';
import { BLUE_COLOR } from "../../constance/color";

export default function InterfaceAxBOMWasteWoodView() {
  const { t } = useTranslation();
  const tableRef = useRef();
  const [openHistory, setOpenHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState(null);
  const [form] = Form.useForm();
  const [date, setDate] = React.useState(null);
  const [dataAvailable, setDataAvailable] = useState(true);
  const isFormValid = useMemo(() => !!date && dataAvailable, [date, dataAvailable]);
  const formattedDate = date ? dayjs(date).format(DATE_FORMAT) : null;

  const handleDoInterfaceAX = async () => {
    setLoading(true);
    setErrorMessages(null);
    try {
      const values = await form.validateFields();
      const formattedDate = dayjs(values.date).format(DATE_FORMAT);
      await POST(URL_INTERFACE_AX.BOM_WASTE_WOOD, { date: formattedDate });
      if (tableRef.current) {
        tableRef.current.clear();
      }
      Toast.info(t("progressing"));
    } catch (error) {
      setErrorMessages(error?.errorMessages || t("error_messages.generic_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (date && tableRef.current) {
      tableRef.current.fetch();
    }
  }, [date]);

  const handleDataCheck = (data) => {
    setDataAvailable(data && data.length > 0);
  };


  const columns = useMemo(
    () => [
      {
        title: t("barcodes.barcode_id"),
        dataIndex: "barcode",
      },
      {
        title: t("post_baking_waste_wood_count.group_date"),
        dataIndex: "post_baking_group_date",
        render: (value) => (value ? dayjs(value).format(HUMAN_DATE_FORMAT) : "-"),
      },
      {
        title: t("wood_size"),
        render: (text, row) => {
          const value = _.get(row, "wood_size_amount_map_list", []).filter((e) => e.is_interfaced_order);
          return value.length ? (
            <List>
              {value
                .filter((data) => _.get(data, "amount", 0))
                .map((data, index) => (
                  <List.Item key={index}>
                    {_.get(data, "wood_code", "")} - {`(${formatComma(_.get(data, "amount", 0))})`} {formatComma(_.get(data, "volume", 0))}
                  </List.Item>
                ))}
            </List>
          ) : (
            "-"
          );
        },
      },
      {
        title: t("interface_ax.is_interfaced"),
        dataIndex: "is_interfaced_bom",
        render: (text, row) => {
          const value = _.get(row, "wood_size_amount_map_list", []).filter((e) => e.is_interfaced_order);
          return value.length ? (
            <List
              dataSource={value.filter((data) => _.get(data, "amount", 0))}
              renderItem={(data) => (
                <div style={{ textAlign: "center" }}>
                  {data.is_interfaced_bom ? <CheckOutlined style={{ color: "green" }} /> : <CloseOutlined style={{ color: "red" }} />}
                </div>
              )}
            />
          ) : (
            "-"
          );
        },
      },
    ],
    [t]
  );

  return (
    <div>
      <MainHeader />
      <div style={{ margin: "24px", marginTop: "7em" }}>
        <Typography.Title level={2}>
          {t("menus.interface_ax")}: {t("menus.interface_ax_menus.bom_waste_wood")}
        </Typography.Title>

        <Form form={form} layout="inline" initialValues={{ date: null }}>
          <Space>
            <Form.Item
              name="date"
              label={t("post_baking_waste_wood_input.input_date")}
              rules={[{ required: true, message: (t('error_messages.validation.required')) }]}
            >
              <DatePicker
                format={HUMAN_DATE_FORMAT}
                placeholder={t("searchs.fields.date")}
                onChange={(value) => setDate(value)}
                allowClear
              />
            </Form.Item>

            <ColorButton
              variant="outlined"
              color='primary'
              override={BLUE_COLOR}
              onClick={() => setOpenHistory(true)}
            >
              {t("interface_ax.history")}
            </ColorButton>

            {!date ? (
              <Tooltip title={`${t("select")} ${t("searchs.fields.date")}`} placement="bottom">
                <ColorButton
                  type="primary"
                  variant="outlined"
                  override={BLUE_COLOR}
                  loading={loading}
                  disabled={!isFormValid}
                  onClick={handleDoInterfaceAX}
                >
                  {t("interface_ax.submit")}
                </ColorButton>
              </Tooltip>
            ) : (
              <ColorButton
                type="primary"
                variant="outlined"
                override={BLUE_COLOR}
                loading={loading}
                disabled={!isFormValid}
                onClick={handleDoInterfaceAX}
              >
                {t("interface_ax.submit")}
              </ColorButton>
            )}

          </Space>
        </Form>

        {errorMessages && <Typography.Text type="danger">{errorMessages}</Typography.Text>}

        <TSTable
          style={{ marginTop: '24px' }}
          ref={tableRef}
          columns={columns}
          url={date ? (URL_POST_BAKING.POST_BAKING_WASTE_WOOD_INPUT_TRANSACTION) : ""}
          manual
          params={{
            input_date: formattedDate ? dayjs(date).format(DATE_FORMAT) : null,
            is_interfaced_order: true,
          }}
          onData={handleDataCheck}
        />
        <InterfaceAXHistoryModal
          open={openHistory}
          onClose={() => setOpenHistory(false)}
          type={INTERFACE_TYPE.BOM_WASTE_WOOD}
        />
      </div>
    </div>
  );
}